/**
 * Dynamic Banners Module
 * Handles loading and displaying dynamic banners based on user's region
 */

class DynamicBanners {
	/**
	 * Get country/region from multiple sources with fallback
	 * Priority: localStorage > FormGeolocation API > default ('au')
	 */
	static async getCountryRegion(isPersonalisationEnabled = true, strictMode = false) {
		if (isPersonalisationEnabled) {
			const coreDnaPerson = JSON.parse(localStorage.getItem("coredna_person"));
			const locationData = coreDnaPerson?.customisation?.location;

			if (locationData?.country) {
				const country = locationData.country.toUpperCase();
				if (country === "NZ") {
					return "nz";
				} else if (country === "AU") {
					const validAUStates = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
					const stateCode = locationData.region?.toUpperCase();
					// Check if state is unidentified for AU
					if (strictMode && (!stateCode || !validAUStates.includes(stateCode))) {
						return null;
					}
					return "au";
				}
				if (strictMode) {
					return null;
				}
			}
		}

		// If not in localStorage, try FormGeolocation API with caching
		const cacheKey = 'dynamicBanners_geolocation_cache';
		const cacheDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
		
		// Check if we have a cached result
		try {
			const cached = JSON.parse(localStorage.getItem(cacheKey));
			if (cached && Date.now() - cached.timestamp < cacheDuration) {
				return cached.region;
			}
		} catch (error) {
			console.warn('[DynamicBanners] Error reading cache:', error);
		}
		
		try {
			const response = await fetch('https://www.coredna.com/geolocation');
			if (response.ok) {
				const data = await response.json();

				if (data.country) {
					const country = data.country.toUpperCase();
					let region = null;
					
					if (country === "NZ") {
						region = "nz";
					} else if (country === "AU") {
						region = "au";
					}
					
					// Cache the result
					if (region) {
						try {
							localStorage.setItem(cacheKey, JSON.stringify({
								region: region,
								timestamp: Date.now()
							}));
						} catch (error) {
							console.warn('[DynamicBanners] Error caching geolocation:', error);
						}
					}
					
					return region;
				}
			}
		} catch (error) {
			console.warn('[DynamicBanners] FormGeolocation API error:', error);
		}

		// Default to AU if no valid region detected
		return 'au';
	}

	/**
	 * Set location filter dropdown and trigger change event
	 */
	static setLocationFilter(region) {
		const locationFilter = document.getElementById('location-filter');
		if (locationFilter) {
			const optionValue = (region === 'nz') ? 'newzealand' : 'all';
			locationFilter.value = optionValue;
			// Trigger change event for coming-up-filter.js listener
			locationFilter.dispatchEvent(new Event('change', {bubbles: true}));
		}
	}

	/**
	 * Load dynamic banners
	 * @param {Object} containerMappings - Map of container IDs to banner IDs
	 * @param {Object} options - Configuration options
	 * @param {boolean} options.setFilter - Whether to set location filter (default: true)
	 * @param {string} options.fetchUrl - Custom fetch URL (default: current page URL with params)
	 */
	static async loadDynamicBanners(containerMappings, options = {}) {
		const {
			setFilter = true,
			fetchUrl = null,
			isPersonalisationEnabled = true
		} = options;

		// Check if any containers exist in DOM
		const availableContainers = {};
		for (const [containerId, bannerId] of Object.entries(containerMappings)) {
			const container = document.getElementById(containerId);
			if (container) {
				availableContainers[containerId] = {container, bannerId};
			} else {
				console.warn('[DynamicBanners] Container not found in DOM:', containerId);
			}
		}

		// If no containers found, return early
		if (Object.keys(availableContainers).length === 0) {
			console.warn('[DynamicBanners] No containers found in DOM');
			return;
		}

		const region = await this.getCountryRegion(isPersonalisationEnabled);

		// Set location filter if enabled
		if (setFilter) {
			this.setLocationFilter(region);
		}

		// Build fetch URL
		let url = fetchUrl;
		if (!url) {
			// Use current page URL
			const currentUrl = window.location.pathname + window.location.search;
			const separator = window.location.search ? '&' : '?';
			url = currentUrl + separator + 'loadDynamicBanners=true&region=' + region;
		} else {
			// Replace {region} placeholder if present
			url = url.replace('{region}', region);
		}

		try {
			const response = await fetch(url);
			const html = await response.text();
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, 'text/html');

			// Populate each container if its corresponding banner exists in response
			for (const [containerId, {container, bannerId}] of Object.entries(availableContainers)) {
				const bannerElement = doc.querySelector('#' + bannerId);
				if (bannerElement) {
					container.innerHTML = bannerElement.innerHTML;
				} else {
					console.warn('[DynamicBanners] Banner not found in response:', bannerId);
				}
			}
		} catch (error) {
			console.error('[DynamicBanners] Failed to load banners:', error);
		}
	}
}

// Attach to window for global access
window.DynamicBanners = DynamicBanners;
